export const supportedLocales = ['ko', 'en'] as const;

export type AppLocale = (typeof supportedLocales)[number];

export const DEFAULT_LOCALE: AppLocale = 'ko';
export const LOCALE_STORAGE_KEY = 'kookjetnc.locale';

const LOCALE_PREFIX_PATTERN = /^\/(en)(?=\/|$)/;

export const isSupportedLocale = (value: string): value is AppLocale => {
  return supportedLocales.some((locale) => locale === value);
};

export const getLocaleFromPathname = (pathname: string): AppLocale | null => {
  const match = pathname.match(LOCALE_PREFIX_PATTERN);
  const locale = match?.[1];

  return locale && isSupportedLocale(locale) ? locale : null;
};

export const getPathWithoutLocalePrefix = (pathname: string) => {
  const match = pathname.match(LOCALE_PREFIX_PATTERN);

  if (!match) {
    return pathname || '/';
  }

  const nextPathname = pathname.slice(match[0].length);
  return nextPathname.length > 0 ? nextPathname : '/';
};

const splitPathAndSuffix = (path: string) => {
  const match = path.match(/^([^?#]*)([?#].*)?$/);
  const pathname = match?.[1] && match[1].length > 0 ? match[1] : '/';
  const suffix = match?.[2] ?? '';

  return {
    pathname,
    suffix,
  };
};

const isExternalPath = (path: string) => {
  return /^(?:[a-z][a-z\d+\-.]*:|\/\/|#)/i.test(path);
};

export const toLocalizedPath = (path: string, locale: AppLocale) => {
  if (isExternalPath(path)) {
    return path;
  }

  const { pathname, suffix } = splitPathAndSuffix(path);
  const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const basePathname = getPathWithoutLocalePrefix(normalizedPathname);

  if (locale === DEFAULT_LOCALE) {
    return `${basePathname}${suffix}`;
  }

  return basePathname === '/' ? `/${locale}${suffix}` : `/${locale}${basePathname}${suffix}`;
};

export const getStoredLocale = (): AppLocale | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  return value && isSupportedLocale(value) ? value : null;
};

export const setStoredLocale = (locale: AppLocale) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
};

export const getCurrentLocale = (): AppLocale => {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  return getLocaleFromPathname(window.location.pathname) ?? getStoredLocale() ?? DEFAULT_LOCALE;
};
